import { api } from '../../core/network/api';

/** GET /api/account/me — everything the Settings page shows about the account. */
export interface AccountInfo {
  email: string;
  username: string | null;
  role: 'PLAYER' | 'ADMIN' | 'KYC_ADMIN' | string;
  createdAt: string;
  emailVerified: boolean;
  phone: string | null;
  /** Editable profile fields (Settings → Profile). Null until set. */
  firstName: string | null;
  lastName: string | null;
  /** Billing country (ISO-2) — drives invoice VAT. */
  country: string | null;
  /** Display language as an underscore id (`en_US`). Null = never chosen. */
  locale: string | null;
  /** Display AND checkout currency (ISO-4217). Sent to the payment provider at checkout. */
  currency: string | null;
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  kycRejectReason: string | null;
  kycSubmittedAt: string | null;
  /** `feature.kyc` game setting — hides the Papers tab content when off. */
  kycEnabled: boolean;
}

export const fetchAccount = () => api.get<AccountInfo>('/account/me');

/** POST /api/account/change-password — verifies the current password first. */
export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post<{ ok: boolean }>('/account/change-password', { currentPassword, newPassword });

/** POST /api/account/resend-verification — re-send the verify-email mail. */
export const resendVerification = () =>
  api.post<{ ok: boolean }>('/account/resend-verification', {});

/** PATCH /api/account/country — billing country (dominion's /user/country). */
export const setCountry = (country: string) =>
  api.patch<{ ok: boolean; country: string }>('/account/country', { country });

/**
 * PATCH /api/account/profile — editable profile fields. Only send the keys
 * that changed; an empty string clears a field. `phone` is the same column
 * shown at registration — this makes it editable from Settings too.
 */
export const updateProfile = (input: {
  firstName?: string; lastName?: string; phone?: string;
}) => api.patch<{
  ok: boolean;
  firstName: string | null; lastName: string | null; phone: string | null;
}>('/account/profile', input);

/** PATCH /api/account/locale — display language, so it follows the player across devices. */
export const setAccountLocale = (locale: string) =>
  api.patch<{ ok: boolean; locale: string }>('/account/locale', { locale });

/** PATCH /api/account/currency — the player's preferred currency (display + checkout). */
export const setAccountCurrency = (currency: string) =>
  api.patch<{ ok: boolean; currency: string }>('/account/currency', { currency });

/** POST /api/kyc/submit — identity papers (core endpoint, multipart). */
export function submitKyc(input: {
  fullName: string;
  dateOfBirth: string; // yyyy-mm-dd
  address: string;
  country: string; // ISO-2
  document: File;
}): Promise<{ status: string }> {
  const form = new FormData();
  form.set('fullName', input.fullName);
  form.set('dateOfBirth', input.dateOfBirth);
  form.set('address', input.address);
  form.set('country', input.country);
  form.set('document', input.document);
  // Override the client's JSON default so axios sets the multipart boundary.
  return api.post<{ status: string }>('/kyc/submit', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}
